import { useMemo } from "react";

import { isEqual, isNil, omit } from "lodash";
import LZString from "lz-string";
import {
  type QueryParamConfig,
  decodeJson,
  encodeJson,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { type TableColumnWidthInfo } from "@devexpress/dx-react-grid";

import {
  FIXED_NAME_COLUMN_DEFAULT_WIDTH,
  FIXED_NAME_COLUMN_ID,
  FIXED_STATUS_COLUMN_DEFAULT_WIDTH,
  FIXED_STATUS_COLUMN_ID,
} from "../table/components/constants";
import {
  getColumnWidths,
  updateColumnConfigWidth,
} from "../utils/table-state-helpers";

export interface ColumnConfig {
  id: string;
  width: TableColumnWidthInfo["width"];
  showMiniGraph: boolean;
}

export type TableState = {
  fixedColumnWidths: {
    [FIXED_NAME_COLUMN_ID]: TableColumnWidthInfo["width"];
    [FIXED_STATUS_COLUMN_ID]: TableColumnWidthInfo["width"];
  };
  columnConfigs: ColumnConfig[];
};
const DEFAULT_TABLE_STATE: TableState = {
  fixedColumnWidths: {
    [FIXED_NAME_COLUMN_ID]: FIXED_NAME_COLUMN_DEFAULT_WIDTH,
    [FIXED_STATUS_COLUMN_ID]: FIXED_STATUS_COLUMN_DEFAULT_WIDTH,
  },
  columnConfigs: [],
};
export const isDefaultTableState = (tableState: TableState) =>
  isEqual(tableState, DEFAULT_TABLE_STATE);

export const TableStateCustomJsonCompressedParam =
  (): QueryParamConfig<TableState> => ({
    encode: (value) => {
      try {
        // New format [id: string, width: number, showMiniGraph: 0 | 1]
        const superCompressed = {
          ...value,
          columnConfigs: value.columnConfigs.map((i) => [
            i.id,
            i.width,
            i.showMiniGraph ? 1 : 0,
          ]),
        };
        const stringified = encodeJson(superCompressed) as string;
        const compressed = LZString.compressToEncodedURIComponent(stringified);
        return compressed;
      } catch (error) {
        return "";
      }
    },
    decode: (value) => {
      if (isNil(value)) {
        return value;
      }
      try {
        const decodedSuperCompressed = decodeJson(
          LZString.decompressFromEncodedURIComponent(value as string),
        );

        return {
          ...decodedSuperCompressed,
          columnConfigs: decodedSuperCompressed.columnConfigs
            .map((i: any) => {
              try {
                // New shorter stringified format
                if (Array.isArray(i)) {
                  const [id, width, showMiniGraph] = i;
                  return {
                    id,
                    width,
                    showMiniGraph: showMiniGraph === 1,
                  };
                }

                // Backwards compatibility
                if (!isNil(i.id) && !isNil(i.w) && !isNil(i.g)) {
                  return {
                    id: i.id,
                    width: i.w,
                    showMiniGraph: Boolean(i.g),
                  };
                }

                return null;
              } catch (error) {
                return null;
              }
            })
            .filter(Boolean),
        };
      } catch (error) {
        return {
          fixedColumnWidths: {
            [FIXED_NAME_COLUMN_ID]: FIXED_NAME_COLUMN_DEFAULT_WIDTH,
            [FIXED_STATUS_COLUMN_ID]: FIXED_STATUS_COLUMN_DEFAULT_WIDTH,
          },
          columnConfigs: [],
        };
      }
    },
    equals: (a, b) => isEqual(a, b),
  });

export const TableStateJsonParam: QueryParamConfig<TableState> = withDefault(
  TableStateCustomJsonCompressedParam(),
  DEFAULT_TABLE_STATE,
);

export const useTableState = () => {
  const [tableState, setTableState] = useQueryParam<TableState>(
    "tableState",
    TableStateJsonParam,
  );

  const columnWidths = useMemo<TableColumnWidthInfo[]>(
    () => getColumnWidths(tableState),
    [tableState],
  );

  return {
    tableState,
    setTableState,
    columnConfigs: tableState.columnConfigs,
    setColumnConfigs: (newColumnConfigs: ColumnConfig[]) =>
      setTableState(
        (prevTableState) => ({
          ...prevTableState,
          columnConfigs: newColumnConfigs.map((i) => omit(i, "name")),
        }),
        "pushIn",
      ),
    columnWidths,
    updateColumnConfigWidth: (
      ...args: Parameters<typeof updateColumnConfigWidth>
    ) => setTableState(updateColumnConfigWidth(...args), "pushIn"),
  };
};
